import * as React from 'react'
import { cn } from '../../../lib/utils'

export interface ChatChannel {
	id: string
	label: string
	unread?: number
	/** Source logo shown before the label — falls back to plain text when absent. */
	logo?: string
}

export interface ChatChannelTabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
	channels: ChatChannel[]
	activeId: string
	onChange: (id: string) => void
	/**
	 * Rendered after the last tab — this is where "add a channel" belongs.
	 *
	 * A slot rather than an `onAdd` callback: the consumer's button opens a
	 * menu, so it has to be the dropdown's own trigger. The callback version
	 * could only render a plain button, so consumers built their own next to
	 * this component instead, which pushed it away from the tabs it belongs to
	 * and left a dead `+` in here.
	 */
	addSlot?: React.ReactNode
}

const ChatChannelTabs = React.forwardRef<HTMLDivElement, ChatChannelTabsProps>(
	({ className, channels, activeId, onChange, addSlot, ...props }, ref) => (
		<div
			ref={ref}
			data-slot="chat-channel-tabs"
			className={cn('flex items-center gap-1 overflow-x-auto', className)}
			{...props}
		>
			{channels.map((c) => {
				const isActive = c.id === activeId
				return (
					<button
						key={c.id}
						type="button"
						onClick={() => onChange(c.id)}
						className={cn(
							'flex shrink-0 items-center gap-1.5 rounded-(--radius-sm) px-3 py-1.5 text-sm font-[500] transition-colors',
							isActive
								? 'bg-(--color-muted) text-(--color-foreground)'
								: 'text-(--color-muted-foreground) hover:text-(--color-foreground)'
						)}
					>
						{c.logo && (
							<img src={c.logo} alt="" className="size-4 shrink-0 rounded-[3px] object-contain" />
						)}
						{c.label}
						{c.unread != null && c.unread > 0 && (
							<span className="inline-flex min-w-4 items-center justify-center rounded-full bg-(--color-destructive) px-1 text-xs leading-4 text-(--color-destructive-foreground)">
								{c.unread}
							</span>
						)}
					</button>
				)
			})}
			{addSlot}
		</div>
	)
)

ChatChannelTabs.displayName = 'ChatChannelTabs'
export { ChatChannelTabs }
